import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {Observable, of, ReplaySubject, switchMap, tap, throwError} from 'rxjs';
import {catchError, finalize, map, shareReplay} from 'rxjs/operators';

import {environment} from '@env/environment';
import {TBankAccountSource} from '@common/types';
import {IBankAutoStatements, TAutoStatementsSetting} from '@models/auto-statements.model';
import {BankAccountListModel, IBankAccount as IBankAccountLight} from '@models/bank-account-list.model';
import {IFee, IInstitution, IMxInstitutionResponse, IPdfInstitutionResponse} from '../../bank-account-list/add-connection/interface';
import {getHttpParams} from '@shared/common-functions.util';
import {Socket} from '../../pages/utils';
import {SidebarService} from '@services/sidebar.service';
import {NotificationService} from '@notifications';

export type TStatementConfidenceTier = 'HIGH' | 'MEDIUM' | 'LOW';

export type TConnectSessionStatus =
  | 'AWAITING_AVAILABILITY'
  | 'SCHEDULED'
  | 'OFFERED_RESCHEDULE'
  | 'DEFERRED'
  | 'CONNECTING'
  | 'AWAITING_DELIVERY_CHOICE'
  | 'AWAITING_CODE'
  | 'AWAITING_OAUTH'
  | 'SUBMITTED'
  | 'COMPLETED'
  | 'NEEDS_HUMAN'
  | 'FAILED'
  | 'EXPIRED'
  | 'DECLINED'
  | 'CANCELLED';

// ACCOUNTANT - a person started it (Request MFA button); AUTO - the system started it off a bank challenge;
// null/absent - a session from before this field existed, origin unknown.
export type TConnectSessionOrigin = 'ACCOUNTANT' | 'AUTO';

export interface IConnectSession {
  sessionId: number;
  status: TConnectSessionStatus;
  origin?: TConnectSessionOrigin | null;
  phoneTo: string | null;
  resumeAt: string | null;
  snoozeCount: number;
  error: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * FDE connection capabilities. Each flag is a nullable boolean:
 * null - not supported by this bank, true - enabled, false - supported but turned off.
 */
export interface IFdeCapabilities {
  stmtFlag: boolean | null;
  imgFlag: boolean | null;
  txFlag: boolean | null;
}

export interface IStatementFallbackInstitution {
  institutionId: string;
  name: string;
  logoUrl: string;
  providerLabel: string;
}

/** premium statement-fallback suggestion for a non-FDE connection that can't provide statements */
export interface IStatementFallback {
  institutionName: string;
  fallback: IStatementFallbackInstitution | null;
  fallbackCandidates: number;
}

export interface IBankAccount {
  id: number;
  name: string;
  accountId: string;
  accountNumber: string;
  allowStatements?: boolean;
  oauthEnabled?: boolean;
  source?: TBankAccountSource;
  lastTransactionRefreshDate?: string | null;
  lastStatementRefreshDate?: string | null;
  lastTransactionDate?: string | null;
  oldestTransactionDate?: string | null;
  currency?: string | null;
  type?: string | null;
  accountNumberDisplay?: string | null;
  nextExpectedStatementDate?: string | null;
  statementConfidenceTier?: TStatementConfidenceTier | null;
}

export interface IDuplicateWarningConnection {
  source: TBankAccountSource;
  bankAccountId: number;
  connectionName: string;
  clientName: string | null;
  statusHint: string | null;
  addedAt: string | null;
}

/** One duplicate-connection group (card #984): every listed connection carries the same account. */
export interface IDuplicateWarning {
  institutionName: string;
  accountNumberLast4: string;
  accountName: string | null;
  connections: IDuplicateWarningConnection[];
}

//todo legacy need significant changes
/**
 * bank account service
 */
@Injectable({
  providedIn: 'root'
})
export class BankAccountService {
  banksCache: { [key: string]: BankAccountListModel[] } = {};
  private fdeInstitutionsCache$: Observable<IInstitution[]> | null = null;
  private checkFeesCache$: Observable<IFee[]> | null = null;
  // selected inside bank list
  selectedBankAccount: ReplaySubject<BankAccountListModel> = new ReplaySubject<BankAccountListModel>(1);
  // changed externally from bank list, needed to update bank list ui
  bankAccountChanged: EventEmitter<number> = new EventEmitter<number>();

  socket: Socket;
  reloadClients: string[] = [];

  constructor(
    private http: HttpClient,
    private sidebarService: SidebarService,
    private notificationService: NotificationService,
  ) {
  }

  selectBankAccount(bankAccount: BankAccountListModel) {
    this.selectedBankAccount.next(bankAccount);
  }

  mapLSBankAccountToQbo(lsBankAccount, qboBankAccount: number, qboBankAccountName: string): Observable<boolean> {
    return this.http
    .put<boolean>(`${environment.API_URL}/accounts/${lsBankAccount}`, {
      qboBankAccount,
      qboBankAccountName,
    });
  }

  /**
   * get user id from finicity api
   * @param username - selected client username
   */
  getUserId(username): Observable<any> {
    return this.http.post(environment.API_URL + '/accounts/add', {username})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getFinancialDataSourceUrl(
    financialDataSource: TBankAccountSource,
    username: string,
    institutionId?: string,
    widgetSessionId?: string,
  ): Observable<{
    connectUrl: string
  }> {
    return this.http.post<{
      connectUrl: string
    }>(environment.API_URL + `/accounts/add/${financialDataSource}`, {
      username,
      ...(institutionId ? {institutionId} : {}),
      ...(widgetSessionId ? {widgetSessionId} : {}),
    });
  }

  initiateSocket(clientUsername: string) {
    this.reloadClients.push(clientUsername);
    if (this.socket) {
      // socket already connected
      return;
    }

    const connect = () => {
      this.socket = new Socket(`${environment.WS_URL}/widget-messages`);
      this.socket.on('message', onMessage);
      this.socket.on('close', onClose);
    };

    const onMessage = (event: any) => {
      // {"message":"reload dashboard","username":"mauriceb"}
      let {data} = event;
      if (typeof data === 'string') {
        try {
          data = JSON.parse(data);
        } catch {
          data = {};
        }
      }

      if (
        data.message === 'reload dashboard' && this.reloadClients.includes(data.username)
      ) {
        this.sidebarService.reloadBankList(data.connectionId);
        this.socket.disconnect();
        delete this.socket;
      }
    };

    const onClose = () => {
      this.socket.disconnect();
    };

    connect();
  }

  // `source` echoes back which source the fix will actually run against — the backend may switch a
  // broken connection to a different one (e.g. FINICITY -> MX) transparently, so callers must launch
  // the widget for the returned source, not the one they requested with.
  fixAccount(
    bankAccountId: number, source: TBankAccountSource, widgetSessionId?: string,
  ): Observable<{connectUrl: string; source: TBankAccountSource}> {
    return this.http.post<{connectUrl: string; source: TBankAccountSource}>(environment.API_URL + `/accounts/fix/${source}`, {
      bankAccountId,
      ...(widgetSessionId ? {widgetSessionId} : {}),
    });
  }

  getBankAccountsListLight(username): Observable<IBankAccountLight[]> {
    return this.http.get<IBankAccountLight[]>(environment.API_URL + `/accounts?username=${username}`);
  }

  /**
   * get bank account list by username
   * param username
   */
  getBankAccountList(username, bankListChanged = false): Observable<{ banks: BankAccountListModel[] }> {
    if (bankListChanged) {
      delete this.banksCache[username];
    }
    if (username in this.banksCache) {
      return of({banks: this.banksCache[username]});
    }
    const request = this.http.get<{ banks: BankAccountListModel[] }>(
      environment.API_URL + '/accounts/banks', getHttpParams({username})
    ).pipe(
      tap((data) => this.banksCache[username] = data.banks),
      catchError((err) => {
        this.notificationService.error(err);
        return of({banks: []});
      }),
    );
    return request;
  }

  /**
   * get bank account login info
   * param bankAccountId
   * param source
   */
  getBankAccountSummaryInfo(bankAccountId, source): Observable<any> {
    return this.http.get(`${environment.API_URL}/accounts/banks/summary/${source}/${bankAccountId}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * update bank account name
   * @param data - {bankAccountId:accountNumber, name:string}
   * @param source - source
   */
  updateName(data, source) {
    return this.http.put(`${environment.API_URL}/accounts/banks/${source}/update`, data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * update bank account name
   * param data {bankAccountId:accountNumber, name:string}
   */
  updateAccountName(data) {
    return this.http.put(environment.API_URL + '/accounts/name-update', data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  updatePdfAccount(
    accountId: number,
    data: { name: string; accountNumber: string; accountType: string },
  ): Observable<void> {
    return this.http.put<void>(`${environment.API_URL}/accounts/banks/pdf/account/${accountId}`, data);
  }

  /**
   * update credentials data
   * param data {bankAccountId:accountNumber, name:string}
   */
  updateCredentials(data, source) {
    return this.http.put(`${environment.API_URL}/accounts/banks/${source}/credentials/modify`, data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * delete bank account data
   * @param bankAccountId - bankAccountId
   * @param source - source
   */
  deleteBankAccountData(bankAccountId: number, source: string) {
    return this.http.delete(`${environment.API_URL}/accounts/banks/${source}/delete/${bankAccountId}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  deleteConnection(accountId: number, source: TBankAccountSource): Observable<void> {
    return this.http.delete<void>(`${environment.API_URL}/accounts/${accountId}/${source}`);
  }

  hideConnection(accountId: number, source: TBankAccountSource): Observable<void> {
    return this.http.put<void>(`${environment.API_URL}/accounts/hide/${accountId}/${source}`, {});
  }

  unhideConnection(accountId: number, source: TBankAccountSource): Observable<void> {
    return this.http.put<void>(`${environment.API_URL}/accounts/unhide/${accountId}/${source}`, {});
  }

  /**
   * delete individual account
   * @param accountId
   * @param confirmed
   */
  deleteAccount(accountId: number, confirmed: boolean = false) {
    return this.http.delete(`${environment.API_URL}/accounts/delete/${accountId}?confirmed=${confirmed}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * updateBalance
   * param bankAccountId
   */
  updateBalance(bankAccountId: number, source: string = 'FINICITY') {
    return this.http.post(`${environment.API_URL}/accounts/banks/${source}/update-balance/${bankAccountId}`, {})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * refresh bank account data
   * param bankAccountId
   * /api/accounts/banks/{source}/refresh/{bankAccountId}
   */
  refreshBankAccountData(
    bankAccountId: number,
    source: TBankAccountSource,
    dateRange?: { dateFrom: string; dateTo: string },
  ) {
    return this.http.post(`${environment.API_URL}/accounts/banks/${source}/refresh/${bankAccountId}`, dateRange || {})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * get bank sub accounts
   * @param bankAccountId - bank Account Id for get sub account
   * @param username - username
   * @param financialDataSource - FINICITY or FDE
   */
  getBankSubAccounts(
    bankAccountId: number,
    username: string,
    financialDataSource: TBankAccountSource,
  ): Observable<IBankAccount[]> {
    return this.http.get<IBankAccount[]>(
      `${environment.API_URL}/accounts/bank-accounts/${bankAccountId}/${financialDataSource}`,
      getHttpParams({username})
    )
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getFdeInstitution(forceReload: boolean = false): Observable<IInstitution[]> {
    if (forceReload || !this.fdeInstitutionsCache$) {
      this.fdeInstitutionsCache$ = this.http
      .get<IInstitution[]>(`${environment.API_URL}/accounts/banks/institutions?bankFlag=true&ccFlag=true`)
      .pipe(
        map((data) => data.sort((item1, item2) => item1.name > item2.name ? 1 : -1)),
        catchError((error: HttpErrorResponse) => {
          this.fdeInstitutionsCache$ = null;
          return throwError(error);
        }),
        shareReplay(1),
      );
    }

    return this.fdeInstitutionsCache$!;
  }

  getMxInstitution(name?: string): Observable<IMxInstitutionResponse> {
    return this.http.get<IMxInstitutionResponse>(`${environment.API_URL}/institutions/MX`, getHttpParams({name}));
  }

  getPdfInstitution(name?: string, page: number = 0, size: number = 20): Observable<IPdfInstitutionResponse> {
    return this.http.get<IPdfInstitutionResponse>(
      `${environment.API_URL}/institutions/PDF`,
      getHttpParams({name, page, size}),
    );
  }

  getFeaturedPdfInstitutions(): Observable<IPdfInstitutionResponse> {
    return this.http.get<IPdfInstitutionResponse>(
      `${environment.API_URL}/institutions/PDF`,
      getHttpParams({featured: true}),
    );
  }

  /**
   * get sub accounts for FDE bank
   */
  getFdeAccounts(bankId) {
    return this.http.get(`${environment.API_URL}/accounts/${bankId}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * create fde bank account
   * param data
   */
  createFdeBankAccount(data) {
    return this.http.post(`${environment.API_URL}/accounts/banks`, data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  createPdfBankAccount(data: { username: string; institutionId: number; bankAccountName: string }): Observable<{
    bankAccountId: number;
    institutionId: number;
  }> {
    return this.http.post<{
      bankAccountId: number;
      institutionId: number;
    }>(`${environment.API_URL}/accounts/banks/pdf`, data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * answer in security question
   * @param bankAccountId - bank Account Id for get sub account
   * @param data - username
   */
  createFdeSubAccount(data, bankAccountId) {
    return this.http.post(`${environment.API_URL}/accounts/banks/FDE/refresh/${bankAccountId}/sq`, data)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  /**
   * Send user response to FDE
   * @param response
   * @param connectionId
   */
  sendResponse(response, connectionId) {
    return this.http.post(`${environment.API_URL}/accounts/banks/FDE/refresh/${connectionId}/sq`, response);
  }

  /**
   * Cancel an update
   * @param bankAccountId - bank Account Id
   */
  cancelFDERefresh(bankAccountId) {
    return this.http.post(`${environment.API_URL}/accounts/banks/refresh/cancel/${bankAccountId}`, {})
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getBalanceByDate(accountId, balanceDate): Observable<any> {
    return this.http.get(`${environment.API_URL}/accounts/getByBalanceDate?accountId=${accountId}&balanceDate=${balanceDate}`)
    .pipe(catchError((error: HttpErrorResponse) => throwError(error)));
  }

  getCheckFees(forceReload: boolean = false): Observable<IFee[]> {
    if (forceReload || !this.checkFeesCache$) {
      this.checkFeesCache$ = this.http
      .get<IFee[]>(`${environment.API_URL}/fees/check`)
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.checkFeesCache$ = null;
          return throwError(error);
        }),
        shareReplay(1),
      );
    }

    return this.checkFeesCache$!;
  }

  getRecentlyAddedFinicityBank(username: string): Observable<any> {
    return this.http.get(`${environment.API_URL}/accounts/banks/recently-added?username=${username}`);
  }

  /**
   * Duplicate ("parallel") connections for a client (card #984): the same underlying account carried
   * by 2+ connections with overlapping capability. Empty list = nothing parallel.
   */
  getDuplicateWarnings(username: string): Observable<IDuplicateWarning[]> {
    return this.http.get<IDuplicateWarning[]>(
      `${environment.API_URL}/accounts/banks/duplicate-warnings`, getHttpParams({username}));
  }

  // Same source-switch caveat as fixAccount(): the returned `source` is the one the fixUrl was
  // actually issued for.
  getFixConnectionUrl(
    connectionId: number, source: TBankAccountSource,
  ): Observable<{ fixUrl: string; source: TBankAccountSource }> {
    return this.http.get<{ fixUrl: string; source: TBankAccountSource }>(
      `${environment.API_URL}/accounts/fix-url/${connectionId}/${source}`);
  }

  getConnectionUrl(institutionId: string, clientId: number, source: TBankAccountSource): Observable<{ connectUrl: string }> {
    return this.http.get<{ connectUrl: string }>(environment.API_URL + `/accounts/connect-url/${clientId}/${source}`, getHttpParams({institutionId}));
  }

  getAutoStatements(source: string, bankAccountId: number): Observable<IBankAutoStatements> {
    return this.http.get<IBankAutoStatements>(`${environment.API_URL}/accounts/banks/${source}/${bankAccountId}/automatic-statements`);
  }

  /**
   * a connection's own statement auto-pull choice
   * @param source - the connection's source (FINICITY, MX)
   * @param bankAccountId - the connection
   * @param enabled - on, off, or null to follow its client (or the firm)
   */
  setAutoStatements(source: string, bankAccountId: number, enabled: TAutoStatementsSetting): Observable<IBankAutoStatements> {
    return this.http.put<IBankAutoStatements>(
      `${environment.API_URL}/accounts/banks/${source}/${bankAccountId}/automatic-statements`, {enabled});
  }

  updateAccess(what: string, bankAccountId: number, source: string, value: boolean) {
    return this.http.patch(`${environment.API_URL}/accounts/banks/${source}/${bankAccountId}/${what}`, {value});
  }

  copyConnection(source: TBankAccountSource, connectionId: number, targetClientId: number) {
    return this.http.post(environment.API_URL + `/accounts/banks/${source}/${connectionId}/copy`, {targetClientId});
  }

  /**
   * get FDE connection capabilities (statements/images/transactions)
   * @param connectionId
   */
  getFdeCapabilities(connectionId: number): Observable<IFdeCapabilities> {
    return this.http.get<IFdeCapabilities>(`${environment.API_URL}/accounts/banks/FDE/${connectionId}/capabilities`);
  }

  /**
   * turn on/off an FDE connection capability
   * @param connectionId
   * @param body - partial capabilities to update, e.g. {imgFlag: true}
   */
  updateFdeCapabilities(connectionId: number, body: Partial<IFdeCapabilities>): Observable<void> {
    return this.http.put<void>(`${environment.API_URL}/accounts/banks/FDE/${connectionId}/capabilities`, body);
  }

  /**
   * get a premium statement-fallback suggestion for a non-FDE connection that can't provide statements
   * @param source
   * @param connectionId
   */
  getStatementFallback(source: TBankAccountSource, connectionId: number): Observable<IStatementFallback> {
    return this.http.get<IStatementFallback>(`${environment.API_URL}/accounts/banks/${source}/${connectionId}/statement-fallback`);
  }

  /**
   * get the current connect session, or null if none has been triggered
   * @param source
   * @param connectionId
   */
  getConnectSession(source: TBankAccountSource, connectionId: number): Observable<IConnectSession | null> {
    return this.http.get<IConnectSession | null>(`${environment.API_URL}/connect-sessions/${source}/${connectionId}`);
  }

  cancelConnectSession(source: TBankAccountSource, connectionId: number, sessionId: number): Observable<void> {
    return this.http.delete<void>(`${environment.API_URL}/connect-sessions/${source}/${connectionId}/${sessionId}`);
  }
}
